import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:9000";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const adminApi = {
  login: (email, password) => api.post("/api/admin/login", { email, password }),
  
  getMe: () => api.get("/api/admin/me"),
  
  getDashboard: () => api.get("/api/admin/dashboard"),
  
  getUsers: (params = {}) => api.get("/api/admin/users", { params }),
  
  getUserById: (id) => {
    // Normalize id by removing leading slashes to prevent double slashes in URL
    const normalizedId = id.replace(/^\/+/, '');
    return api.get(`/api/admin/users/${normalizedId}`);
  },
  
  getAuditLogs: (params = {}) => {
    // Convert array parameters to comma-separated strings
    const serializedParams = { ...params };
    if (Array.isArray(serializedParams.userId)) {
      serializedParams.userId = serializedParams.userId.join(',');
    }
    if (Array.isArray(serializedParams.method)) {
      serializedParams.method = serializedParams.method.join(',');
    }
    if (Array.isArray(serializedParams.entity)) {
      serializedParams.entity = serializedParams.entity.join(',');
    }
    if (Array.isArray(serializedParams.responseStatus)) {
      serializedParams.responseStatus = serializedParams.responseStatus.join(',');
    }
    return api.get("/api/admin/audit-logs", { params: serializedParams });
  },
  
  getAuditLogStats: (params = {}) => api.get("/api/admin/audit-logs/stats", { params }),
  
  getAuditLogFilters: () => api.get("/api/admin/audit-logs/filters"),

  getSourcesList: () => api.get("/api/admin/audit-logs/sources"),

  getGroupedAuditLogs: (params = {}) => {
    const serializedParams = { ...params };
    if (Array.isArray(serializedParams.userId)) {
      serializedParams.userId = serializedParams.userId.join(',');
    }
    if (Array.isArray(serializedParams.method)) {
      serializedParams.method = serializedParams.method.join(',');
    }
    if (Array.isArray(serializedParams.endpoint)) {
      serializedParams.endpoint = serializedParams.endpoint.join(',');
    }
    if (Array.isArray(serializedParams.responseStatus)) {
      serializedParams.responseStatus = serializedParams.responseStatus.join(',');
    }
    return api.get("/api/admin/audit-logs/grouped", { params: serializedParams });
  },
  getUsersList: (params = {}) => {
    const serializedParams = { ...params };
    if (Array.isArray(serializedParams.method)) {
      serializedParams.method = serializedParams.method.join(',');
    }
    if (Array.isArray(serializedParams.endpoint)) {
      serializedParams.endpoint = serializedParams.endpoint.join(',');
    }
    if (Array.isArray(serializedParams.responseStatus)) {
      serializedParams.responseStatus = serializedParams.responseStatus.join(',');
    }
    return api.get("/api/admin/audit-logs/users-list", { params: serializedParams });
  },
  getEndpointsList: (params = {}) => {
    const serializedParams = { ...params };
    if (Array.isArray(serializedParams.method)) {
      serializedParams.method = serializedParams.method.join(',');
    }
    if (Array.isArray(serializedParams.responseStatus)) {
      serializedParams.responseStatus = serializedParams.responseStatus.join(',');
    }
    return api.get("/api/admin/audit-logs/endpoints-list", { params: serializedParams });
  },
  getLogsByUser: (userId, params = {}) => {
    const serializedParams = { ...params };
    if (Array.isArray(serializedParams.method)) {
      serializedParams.method = serializedParams.method.join(',');
    }
    if (Array.isArray(serializedParams.endpoint)) {
      serializedParams.endpoint = serializedParams.endpoint.join(',');
    }
    if (Array.isArray(serializedParams.responseStatus)) {
      serializedParams.responseStatus = serializedParams.responseStatus.join(',');
    }
    // Normalize userId by removing leading slashes to prevent double slashes in URL
    const normalizedUserId = userId.replace(/^\/+/, '');
    return api.get(`/api/admin/audit-logs/by-user/${normalizedUserId}`, { params: serializedParams });
  },
  getLogsByEndpoint: (endpointName, params = {}) => {
    const serializedParams = { ...params };
    if (Array.isArray(serializedParams.method)) {
      serializedParams.method = serializedParams.method.join(',');
    }
    if (Array.isArray(serializedParams.responseStatus)) {
      serializedParams.responseStatus = serializedParams.responseStatus.join(',');
    }
    // Normalize endpointName by removing leading slashes to prevent double slashes in URL
    const normalizedEndpointName = endpointName.replace(/^\/+/, '');
    return api.get(`/api/admin/audit-logs/by-endpoint/${normalizedEndpointName}`, { params: serializedParams });
  },

  getAnalyticsOverview: () => api.get("/api/admin/analytics/overview"),
  
  getUserGrowth: (period) => api.get("/api/admin/analytics/users", { params: { period } }),
  
  getApiUsage: (period) => api.get("/api/admin/analytics/api-usage", { params: { period } }),
  
  getApiCalls: (period) => api.get("/api/admin/analytics/api-calls", { params: { period } }),
  
  getApiErrors: (period) => api.get("/api/admin/analytics/api-errors", { params: { period } }),

  // Contact APIs
  getContactQueries: (params = {}) => api.get("/api/contact/admin/all", { params }),
  
  getContactStats: () => api.get("/api/contact/admin/stats"),
  
  getContactById: (id) => {
    const normalizedId = id.replace(/^\/+/, '');
    return api.get(`/api/contact/admin/view/${normalizedId}`);
  },
};

export default api;
